import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppForm, {
  buildInitialForm,
  mapAppToForm,
  normalizeAppFormPayload,
} from '../components/AppForm';
import { validateCustomFlags } from '../components/CustomFlags';
import { getPlatform } from '../platforms';
import PlatformIcon from '../components/PlatformIcon';
import TopBar from '../components/TopBar';

axios.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem('token') || ''}`;

const EDITOR_TABS = {
  android: [
    { key: 'general', label: 'Settings' },
    { key: 'filters', label: 'Filters' },
    { key: 'uac', label: 'Attribution' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'flags', label: 'Custom flags' },
  ],
  ios: [
    { key: 'general', label: 'Settings' },
    { key: 'asa', label: 'ASA' },
    { key: 'uac', label: 'Attribution' },
    { key: 'flags', label: 'Custom flags' },
  ],
};

function stableKey(value) {
  if (Array.isArray(value)) return value.map(stableKey);
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = stableKey(value[key]);
        return acc;
      }, {});
  }
  return value === undefined ? null : value;
}

function serialize(value) {
  return JSON.stringify(stableKey(value));
}

function describeApiError(err, fallback) {
  const error = err?.response?.data?.error;
  if (!Array.isArray(error)) return error || fallback;

  const issues = error
    .map((issue) => [issue?.path?.join('.'), issue?.message].filter(Boolean).join(': '))
    .filter(Boolean);

  return issues.length ? issues.join('; ') : fallback;
}

export default function AppEditorPage({ mode }) {
  const { platform: platformKey, appId } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === 'edit';
  const platform = getPlatform(platformKey);

  const [formData, setFormData] = useState(buildInitialForm);
  const [baseline, setBaseline] = useState(() => serialize(buildInitialForm()));
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('general');

  const editorTabs = EDITOR_TABS[platformKey] || EDITOR_TABS.android;

  const isDirty = useMemo(() => serialize(formData) !== baseline, [formData, baseline]);

  const pageTitle = useMemo(
    () => (isEdit ? (formData.appId || 'Редактирование') : `Новая ${platform?.label || ''} апка`),
    [isEdit, formData.appId, platform]
  );

  useEffect(() => {
    if (!platform) navigate('/', { replace: true });
  }, [platform, navigate]);

  useEffect(() => {
    if (!isEdit || !appId || !platform) return undefined;

    let ignore = false;

    const loadApp = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await axios.get(platform.item(appId));

        if (!ignore) {
          const loaded = mapAppToForm(res.data);
          setFormData(loaded);
          setBaseline(serialize(loaded));
        }
      } catch (err) {
        console.error('[AppEditorPage] Failed to load app:', err);
        if (!ignore) {
          setError(describeApiError(err, 'Не удалось загрузить приложение'));
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadApp();
    return () => {
      ignore = true;
    };
  }, [isEdit, appId, platform]);

  const handleSave = async () => {
    if (!platform) return;

    const payload = normalizeAppFormPayload(formData, platformKey);

    if (!payload.appId) {
      window.alert('App ID обязателен');
      return;
    }

    if (!payload.appUrl) {
      window.alert('App URL обязателен');
      return;
    }

    const flagsError = validateCustomFlags(payload.customFlags);
    if (flagsError) {
      setActiveSection('flags');
      window.alert(flagsError);
      return;
    }

    try {
      setSaving(true);
      setError('');

      if (isEdit) {
        await axios.put(platform.item(appId), payload);
      } else {
        await axios.post(platform.collection, payload);
      }

      navigate('/', { replace: true });
    } catch (err) {
      console.error('[AppEditorPage] Failed to save app:', err);
      setError(describeApiError(err, 'Не удалось сохранить приложение'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TopBar />

      <div className="container app-editor-page">
      <div className="app-editor-shell compact">
        <div className="ed-head">
          <div className="ed-head-main">
            <Link to="/" className="ed-back" title="До списку додатків" aria-label="До списку додатків">
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" aria-hidden="true">
                <path
                  d="M14.5 5.5 8 12l6.5 6.5"
                  stroke="currentColor"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>

            <PlatformIcon platform={platformKey} size={22} className="ed-title-icon" />
            <h1 className="ed-title">{pageTitle}</h1>
          </div>

          <div className="ed-actions">
            {isDirty && !loading ? <span className="ed-dirty">Є незбережені зміни</span> : null}
            <button
              type="button"
              className="fbtn is-primary"
              onClick={handleSave}
              disabled={saving || loading || !isDirty}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <div className="main-tabs ed-tabs" role="tablist">
          {editorTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeSection === tab.key}
              className={`main-tab${activeSection === tab.key ? ' is-active' : ''}`}
              onClick={() => setActiveSection(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error ? <div className="alert alert-danger app-editor-alert">{String(error)}</div> : null}

        <div className="app-editor-body">
          {loading ? (
            <div className="app-editor-loading compact">Loading...</div>
          ) : (
            <AppForm
              formData={formData}
              setFormData={setFormData}
              isEdit={isEdit}
              section={activeSection}
              platform={platformKey}
            />
          )}
        </div>
      </div>
      </div>
    </>
  );
}
